import { useEffect, useState } from "react";
import { Navigate, useParams } from "react-router";
import { analytics } from "../../analytics";
import { useLocationWrapper } from "../../hooks/useLocationWrapper";
import { useGetNextState } from "../../hooks/useGetNextState";
import Button from "../ButtonComponent";

interface IState {
  slug: string;
  displayName: string;
  logoUrl: string;
  url: string;
  buttonLabel: string;
  title: string;
  instructions: string;
  confirmationLabel: string;
  continueLabel: string;
}

export function ConnectExternalAction() {
  const { linkToken = "" } = useParams();
  const { state } = useLocationWrapper<IState>();
  const { getNextState, loading } = useGetNextState(linkToken);
  const [hasOpened, setHasOpened] = useState(false);
  const [selfConfirmed, setSelfConfirmed] = useState(false);

  useEffect(() => {
    if (analytics && analytics.page) {
      analytics.page("connect external action");
    }
  }, []);

  if (!state) {
    return <Navigate to={`/${linkToken}`} replace />;
  }

  const openExternalAction = () => {
    if (!state.url) {
      console.error("ConnectExternalAction: missing state.url, refusing to open tab");
      return;
    }
    let parsed: URL;
    try {
      parsed = new URL(state.url);
    } catch {
      console.error("ConnectExternalAction: malformed external action URL, refusing to open tab");
      return;
    }
    if (parsed.protocol !== "https:") {
      console.error(`ConnectExternalAction: refusing non-HTTPS protocol (${parsed.protocol}) for external action URL`);
      return;
    }
    window.open(parsed.href, "_blank", "noopener,noreferrer");
    setHasOpened(true);
  };

  const submitConfirmation = () => {
    getNextState("externalAction", {
      type: "continue",
      data: {
        selfConfirmed,
      },
    });
  };

  return (
    <>
      <div className="hidden tp:block d:block"></div>
      <div className="flex flex-col items-center justify-between gap-8 tp:gap-4 max-w-[40rem]">
        <img src={state.logoUrl} className="mx-auto w-24 h-24" alt={`${state.displayName} logo`} />
        <div className="text-texturePennBlue font-bold text-xl d:text-4xl tp:text-2xl text-center">{state.title}</div>
        <div className="text-texturePennBlue text-center mb-2">{state.instructions}</div>
        {hasOpened && (
          <>
            <button type="button" onClick={openExternalAction} className="text-textureIris underline text-sm">
              {state.buttonLabel}
            </button>
            <label className="w-full sm:w-[30rem] flex gap-3 text-texturePennBlue cursor-pointer">
              <input
                type="checkbox"
                className="mt-1 h-4 w-4 shrink-0"
                checked={selfConfirmed}
                onChange={(event) => setSelfConfirmed(event.target.checked)}
              />
              <span>{state.confirmationLabel}</span>
            </label>
          </>
        )}
      </div>
      <div className="w-full sm:w-[30rem]">
        {!hasOpened ? (
          <Button onClick={openExternalAction}>{state.buttonLabel}</Button>
        ) : (
          <Button onClick={submitConfirmation} disabled={!selfConfirmed || loading} loading={loading}>
            {state.continueLabel}
          </Button>
        )}
      </div>
    </>
  );
}
